<template>
  <Transition name="slide-fade" appear>
    <div v-if="visible" class="modal-end">
      <div class="modal-content">
        <div class="modal-step-1">
          <div class="modal-header">
            <span class="modal-header-txt">{{$t('ai_video_generation')}}</span>
            <span class="modal-header-description">{{$t('generate_video_from_text')}}</span>
          </div>

          <div class="video-generation-content">
            <!-- Text Input Section -->
            <div class="input-section">
              <span class="section-title">{{$t('explanation_text')}}</span>
              <textarea
                class="form-input video-text-input"
                :placeholder="$t('transcription_placeholder')"
                v-model="videoText"
                :disabled="isGenerating"
                rows="6"
                id="vidgen"
                v-ai-generator="aiSocket"
              ></textarea>
            </div>

            <!-- Video Duration Estimate -->
            <div class="duration-section">
              <div class="duration-info">
                <span class="duration-label">{{$t('estimated_duration')}}: </span>
                <span class="duration-value">{{estimatedDuration}}</span>
              </div>
            </div>

            <!-- Generation Status -->
            <div v-if="videoGenerationStatus" class="video-generation-status">
              <div class="status-row">
                <span class="status-label">{{$t('video_generation_status')}}:</span>
                <span :class="['status-value', `status-${videoGenerationStatus.status}`]">
                  {{getVideoStatusText(videoGenerationStatus.status)}}
                </span>
              </div>

              <div v-if="videoGenerationStatus.progress" class="progress-bar-container">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{width: videoGenerationStatus.progress + '%'}"></div>
                </div>
                <span class="progress-text">{{videoGenerationStatus.progress}}%</span>
              </div>

              <div v-if="videoGenerationStatus.message" class="status-message">
                {{videoGenerationStatus.message}}
              </div>

              <div v-if="videoGenerationStatus.status === 'failed' && videoGenerationStatus.error" class="status-error">
                {{videoGenerationStatus.error}}
              </div>

              <!-- Connection Warning -->
              <div v-if="isConnectionLost && isGenerating" class="connection-warning">
                <div class="warning-icon">⚠️</div>
                <div class="warning-content">
                  <div class="warning-title">{{$t('connection_lost')}}</div>
                  <div class="warning-message">{{$t('connection_lost_message')}}</div>
                </div>
              </div>

              <!-- Last Update Time -->
              <div v-if="lastUpdateTime && isGenerating" class="last-update">
                <span class="update-label">{{$t('last_update')}}:</span>
                <span class="update-time">{{formatLastUpdate(lastUpdateTime)}}</span>
              </div>
            </div>

            <!-- Video Preview (when completed) -->
            <div v-if="videoGenerationStatus?.status === 'completed' && generatedVideoUrl" class="video-preview-section">
              <span class="section-title">{{$t('generated_video')}}</span>
              <div class="video-preview-container">
                <video ref="videoPlayer" controls class="video-preview">
                  {{$t('video_not_supported')}}
                </video>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
              <button
                v-if="!isGenerating && videoGenerationStatus?.status !== 'completed'"
                class="generate-button primary-button"
                @click="startVideoGeneration"
                :disabled="!videoText.trim()"
              >
                <img src="@/assets/images/magicWand.svg" alt="Generate" />
                {{$t('generate_video')}}
              </button>

              <button
                v-if="isGenerating"
                class="cancel-button secondary-button"
                @click="cancelGeneration"
              >
                {{$t('cancel_generation')}}
              </button>

              <button
                v-if="videoGenerationStatus?.status === 'completed'"
                class="use-video-button primary-button"
                @click="useGeneratedVideo"
              >
                {{$t('use_this_video')}}
              </button>

              <button
                v-if="videoGenerationStatus?.status === 'completed'"
                class="regenerate-button secondary-button"
                @click="regenerateVideo"
              >
                {{$t('generate_again')}}
              </button>
            </div>
          </div>

          <div @click="closeModal" class="close-btn">
            <img class="cross" src="../../assets/images/cross.svg" />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import aiService from "@/service/aiService.js";
import Hls from 'hls.js';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    onHide: {
      type: Function,
      default: () => {}
    },
    initialText: {
      type: String,
      default: ""
    },
    onVideoGenerated: {
      type: Function,
      default: () => {}
    },
    aiSocket: {
      type: Object,
      default: {}
    }
  },
  data() {
    return {
      videoText: this.initialText,
      isGenerating: false,
      videoGenerationStatus: null,
      currentQueueId: null,
      generatedVideoUrl: null,
      connectionTimeout: null,
      lastUpdateTime: null,
      isConnectionLost: false,
      hlsInstance: null,
      updateTimer: null
    };
  },
  computed: {
    estimatedDuration() {
      if (!this.videoText || !this.videoText.trim()) {
        return this.$t('duration_not_calculated');
      }

      // More accurate estimation based on words and reading speed
      const words = this.videoText.trim().split(/\s+/).filter(word => word.length > 0);
      const wordCount = words.length;

      if (wordCount === 0) {
        return this.$t('duration_not_calculated');
      }

      // Average speaking rate: 150-160 words per minute
      // We'll use 140 WPM for a more comfortable pace
      const wordsPerMinute = 140;
      const estimatedMinutes = wordCount / wordsPerMinute;

      // Convert to seconds and add buffer for pauses
      let estimatedSeconds = Math.ceil(estimatedMinutes * 60 * 1.2); // 20% buffer for natural pauses

      // Minimum 10 seconds, maximum 120 seconds (2 minutes)
      estimatedSeconds = Math.max(10, Math.min(120, estimatedSeconds));

      if (estimatedSeconds < 60) {
        return `${estimatedSeconds} ${this.$t('seconds')}`;
      } else {
        const minutes = Math.floor(estimatedSeconds / 60);
        const seconds = estimatedSeconds % 60;
        if (seconds === 0) {
          return `${minutes} ${this.$t('minute')}${minutes > 1 ? this.$t('s') : ''}`;
        } else {
          return `${minutes} ${this.$t('minute')}${minutes > 1 ? this.$t('s') : ''} ${seconds} ${this.$t('seconds')}`;
        }
      }
    }
  },
  mounted() {
    const aiTextGenerator = document.getElementById('aiTextGenerator-vidgen');

    if (aiTextGenerator) {
      aiTextGenerator.style.bottom = '0';
    }

    console.log('aiTextGenerator:', aiTextGenerator);
  },
  watch: {
    initialText(newVal) {
      this.videoText = newVal;
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.videoText = this.initialText;
          this.resetGenerationState();

          // Wait for DOM to update, then try to find the element
          this.$nextTick(() => {
            this.setupAiTextGenerator();
          });
        }
      },
      immediate: true
    },
    'aiSocket.state.genvidQueues': {
      handler: function (queues) {
        if (this.currentQueueId && queues[this.currentQueueId]) {
          const queueData = queues[this.currentQueueId];
          this.handleVideoGenerationUpdate(queueData);
        }
      },
      deep: true
    }
  },
  methods: {
    setupAiTextGenerator() {
      const aiTextGenerator = document.getElementById('aiTextGenerator-vidgen');

      if (aiTextGenerator) {
        aiTextGenerator.style.bottom = 'auto';
        aiTextGenerator.style.top = '100%';
        console.log('aiTextGenerator found and styled:', aiTextGenerator);
      } else {
        console.log('aiTextGenerator not found, retrying...');

        setTimeout(() => {
          this.setupAiTextGenerator();
        }, 100);
      }
    },
    async startVideoGeneration() {
      if (!this.videoText.trim() || this.isGenerating) {
        this.$snackbar.add({
          text: this.$t('please_enter_video_explanation'),
          type: 'info',
        });
        return;
      }

      try {
        this.isGenerating = true;
        this.videoGenerationStatus = null;
        this.generatedVideoUrl = null;

        // Connect to socket if not already connected
        if (!this.aiSocket.state.socket) {
          this.aiSocket.connect();
        }

        const user = JSON.parse(localStorage.getItem('user'));
        const userId = user.userId;
        const token = user.token;

        if (userId && token) {
          this.aiSocket.joinGenvidRoom(userId, token);
        }

        // Generate video via API
        const response = await aiService.generateVideo({
          userId: userId,
          prompt: this.videoText,
          videoName: 'Generated Video'
        });

        if (response.data.success) {
          this.currentQueueId = response.data.data.queueId;

          // Subscribe to queue updates
          this.aiSocket.subscribeGenvidQueue(this.currentQueueId);

          this.videoGenerationStatus = {
            status: 'pending',
            message: this.$t('video_generation_queued'),
            progress: 10,
            queueId: this.currentQueueId
          };

          // Start connection monitoring
          this.startConnectionMonitoring();
        } else {
          const apiError = (typeof response.data.error === 'object')
            ? (response.data.error?.message || JSON.stringify(response.data.error))
            : (response.data.error || response.data.message || 'Failed to start video generation');
          throw new Error(apiError);
        }
      } catch (error) {
        console.error('Error generating video:', error);

        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || this.$t('video_generation_failed');

        this.videoGenerationStatus = {
          status: 'failed',
          error: errorMessage
        };
        this.isGenerating = false;

        this.$snackbar.add({
          text: errorMessage,
          type: 'error',
        });
      }
    },

    handleVideoGenerationUpdate(data) {
      console.log('Video generation update:', data);

      // Reset connection monitoring
      this.lastUpdateTime = new Date();
      this.isConnectionLost = false;
      this.resetConnectionTimeout();

      const progressMap = {
        'pending': 10,
        'generating': 50,
        'uploading': 80,
        'completed': 100,
        'failed': 0
      };

      const parsedError = (typeof data.error === 'object')
        ? (data.error?.message || JSON.stringify(data.error))
        : data.error;

      this.videoGenerationStatus = {
        status: data.status,
        message: data.message,
        error: parsedError,
        progress: progressMap[data.status] || 10,
        queueId: data.queueId,
        queueStep: data.queueStep,
        uploadedFileUrl: data.uploadedFileUrl,
        uploadedFileUuid: data.uploadedFileUuid,
        heygenVideoUrl: data.heygenVideoUrl
      };

      if (data.status === 'completed') {
        this.isGenerating = false;
        this.generatedVideoUrl = data.heygenVideoUrl;
        console.log('Generated video URL:', this.generatedVideoUrl);
        this.clearConnectionTimeout();
        this.clearUpdateTimer();
        this.setupHlsPlayer();

        this.$snackbar.add({
          text: this.$t('video_generated_successfully'),
          type: 'success',
        });
      } else if (data.status === 'failed') {
        this.isGenerating = false;
        this.clearConnectionTimeout();
        this.clearUpdateTimer();

        const failMessage = (typeof data.error === 'object')
          ? (data.error?.message || JSON.stringify(data.error))
          : (data.error || data.message || this.$t('video_generation_failed'));

        this.$snackbar.add({
          text: failMessage,
          type: 'error',
        });
      }
    },

    getVideoStatusText(status) {
      const statusMap = {
        'pending': this.$t('video_status_pending'),
        'generating': this.$t('video_status_generating'),
        'uploading': this.$t('video_status_uploading'),
        'completed': this.$t('video_status_completed'),
        'failed': this.$t('video_status_failed'),
      };
      return statusMap[status] || status;
    },

    cancelGeneration() {
      this.isGenerating = false;
      this.videoGenerationStatus = null;
      this.currentQueueId = null;
    },

    useGeneratedVideo() {
      if (this.videoGenerationStatus && this.videoGenerationStatus.status === 'completed') {
        const videoData = {
          attach: this.videoGenerationStatus.uploadedFileUuid,
          file: {
            attachUrl: this.videoGenerationStatus.uploadedFileUrl,
            fillableId: this.videoGenerationStatus.uploadedFileUuid
          }
        };
        this.onVideoGenerated(videoData);
      }
      this.closeModal();
    },

    regenerateVideo() {
      this.resetGenerationState();
      this.startVideoGeneration();
    },

    resetGenerationState() {
      this.isGenerating = false;
      this.videoGenerationStatus = null;
      this.currentQueueId = null;
      this.generatedVideoUrl = null;
    },

    closeModal() {
      this.resetGenerationState();
      this.clearConnectionTimeout();
      this.clearUpdateTimer();
      this.cleanupHlsPlayer();
      this.onHide();
    },

    setupHlsPlayer() {
      this.$nextTick(() => {
        if (this.$refs.videoPlayer && this.generatedVideoUrl) {
          const video = this.$refs.videoPlayer;

          if (this.generatedVideoUrl.includes('.m3u8')) {
            if (Hls.isSupported()) {
              this.hlsInstance = new Hls();
              this.hlsInstance.loadSource(this.generatedVideoUrl);
              this.hlsInstance.attachMedia(video);
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
              video.src = this.generatedVideoUrl;
            }
          } else {
            video.src = this.generatedVideoUrl;
          }
        }
      });
    },

    cleanupHlsPlayer() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },

    startConnectionMonitoring() {
      this.lastUpdateTime = new Date();
      this.resetConnectionTimeout();
      this.startUpdateTimer();
    },

    startUpdateTimer() {
      this.clearUpdateTimer();
      // Update the display every 5 seconds
      this.updateTimer = setInterval(() => {
        // Force reactivity update
        this.$forceUpdate();
      }, 5000);
    },

    clearUpdateTimer() {
      if (this.updateTimer) {
        clearInterval(this.updateTimer);
        this.updateTimer = null;
      }
    },

    resetConnectionTimeout() {
      this.clearConnectionTimeout();
      this.connectionTimeout = setTimeout(() => {
        this.isConnectionLost = true;
      }, 45000);
    },

    clearConnectionTimeout() {
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
    },

    formatLastUpdate(time) {
      const now = new Date();
      const diffInSeconds = Math.floor((now - time) / 1000);

      // Show at least 1 second to avoid showing "0 seconds ago"
      const displaySeconds = Math.max(1, diffInSeconds);

      if (displaySeconds < 60) {
        return this.$t('seconds_ago', { seconds: displaySeconds });
      } else {
        const diffInMinutes = Math.floor(displaySeconds / 60);
        return this.$t('minutes_ago', { minutes: diffInMinutes });
      }
    }
  }
};
</script>

<style scoped>
.modal-end {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.modal-content {
  position: relative;
  background-color: white;
  width: 60%;
  max-width: 800px;
  height: 100%;
  border-radius: 12px;
  padding: 24px;
  overflow-y: auto;
  max-height: calc(100vh - 48px);
}

.modal-content::-webkit-scrollbar {
  width: 15px;
}

.modal-content::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #FFF;
}

.modal-content::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  background-color: #F2F4F7;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cross {
  width: 16px;
  height: 16px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0 20px 0;
  border: none;
}

.modal-header-txt {
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 18px;
  font-style: normal;
  line-height: 24px;
  margin-bottom: 8px;
}

.modal-header-description {
  font-family: euclid_medium, sans-serif;
  color: #667085;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
}

.video-generation-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  font-family: euclid_medium, sans-serif;
  color: #040C2D;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
}

.form-input {
  border: 1px solid #D0D5DD;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.3s;
  font-family: euclid_regular, sans-serif;
  background-color: #F5F5F5;
  color: #282A36;
  outline: none;
}

.form-input:focus {
  border-color: #4A68F8;
  box-shadow: 0 0 0 3px rgba(74, 104, 248, 0.1);
}

.video-text-input {
  min-height: 120px;
  resize: vertical;
  line-height: 1.5;
}

.duration-section {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 8px 0;
}

.duration-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-label {
  font-family: euclid_medium, sans-serif;
  color: #475569;
  font-size: 13px;
}

.duration-value {
  font-family: euclid_semi_bold, sans-serif;
  color: #1E293B;
  font-size: 14px;
}

.video-generation-status {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
}

.progress-text {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #475569;
  min-width: 40px;
  text-align: right;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.status-label {
  font-family: euclid_medium, sans-serif;
  font-size: 13px;
  color: #475569;
}

.status-value {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 12px;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-generating {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-uploading {
  background: #f3e8ff;
  color: #7c3aed;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

.status-failed {
  background: #fee2e2;
  color: #dc2626;
}

.status-message, .status-error {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  margin-top: 8px;
}

.status-message {
  color: #64748b;
}

.status-error {
  color: #dc2626;
}

.video-preview-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-preview-container {
  border-radius: 8px;
  overflow: hidden;
  background-color: #000;
}

.video-preview {
  width: 100%;
  height: auto;
  max-height: 300px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.primary-button, .secondary-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.primary-button {
  background-color: #4A68F8;
  color: white;
}

.primary-button:hover:not(:disabled) {
  background-color: #3B5AF0;
}

.primary-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

.secondary-button {
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
}

.secondary-button:hover {
  background-color: #F3F4F6;
}

.generate-button img, .use-video-button img {
  width: 16px;
  height: 16px;
}

.connection-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
}

.warning-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #92400e;
  margin-bottom: 4px;
}

.warning-message {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #d97706;
  line-height: 1.4;
}

.last-update {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.update-label {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #64748b;
}

.update-time {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #1e293b;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    height: 95%;
  }

  .action-buttons {
    justify-content: stretch;
  }

  .primary-button, .secondary-button {
    flex: 1;
    justify-content: center;
  }
}
</style>
